import * as React from 'react';
import { IList, doesElementContainFocus, List, IPage } from '@fluentui/react';
import {
    GROUPED_COMBOBOX_ITEM_HEIGHT,
    IGroupedComboBoxItem,
    SectionScrollPosition,
    shouldHighlightItem,
} from 'grouped-combobox-core';
import { GroupHeader } from '../GroupedComboBoxGroupHeader';
import { GroupedComboBoxButton } from '../GroupedComboBoxButton';
import { ISectionProps } from './Section.types';
import { ListListener } from './focus/ListListener';
import { useConst, useId, useRefEffect } from '@fluentui/react-hooks';
import { menuBottomPadding } from './SectionedList.styles';
import { getStyles } from './Section.styles';
import { useAnimateListModifications } from '../utils/useAnimateListModifications';
import { useAnimateMaxHeight } from './useAnimateSectionMaxHeight';

function hasFocus(ref: React.RefObject<HTMLElement>): boolean {
    return ref.current !== null && doesElementContainFocus(ref.current);
}

function getGroupedComboBoxItemKey(item: IGroupedComboBoxItem): string {
    return item.itemKey;
}

function createEmptyGroupedComboBoxItem(): IGroupedComboBoxItem {
    return { itemKey: 'EMPTY_ITEM', text: '' };
}

export const Section = (props: ISectionProps): JSX.Element => {
    const {
        items,
        isCollapsed,
        groupKey,
        text,
        headerStyles,
        itemStyles,
        onGroupToggled,
        componentRef,
        selectedKey,
        parentBottomPadding = 0,
        itemHeight = GROUPED_COMBOBOX_ITEM_HEIGHT,
    } = props;

    const enableAnimation = React.useRef(true);

    const listWrapperRef = useRefEffect<HTMLDivElement>((listWrapperElement) => {
        const styles = getComputedStyle(listWrapperElement);
        // If there is added CSS overriding our transition styles, disable animation as the
        // onTransitionEnd callback might not be called
        if (
            parseFloat(styles.transitionDuration) <= 0 ||
            styles.transitionProperty.indexOf('max-height') < 0
        ) {
            enableAnimation.current = false;
        }
    });

    // hold a ref to the internal List control inside GroupedListSection
    const listRef = React.useRef<IList>(null);
    const ref = React.useRef<HTMLDivElement>(null);

    const listListener = useConst(() => new ListListener(listRef, ref));
    listListener.itemCount = items.length;

    // Simple, no argument, function to toggle the collapsed state with no animation
    // We pass this to `useAnimateMaxHeight` to create a function that adds animation
    const toggleGroupWithoutAnimation = React.useCallback(() => {
        onGroupToggled?.(groupKey, !isCollapsed);
    }, [groupKey, isCollapsed, onGroupToggled]);

    const [maxHeight, toggleGroupWithAnimation, onListTransitionEnd] = useAnimateMaxHeight(
        toggleGroupWithoutAnimation,
        items.length * itemHeight,
        parentBottomPadding + menuBottomPadding,
        listWrapperRef
    );

    // When the toggle button is clicked, tell the parent context through the `toggleGroupWithAnimation` function
    const onToggleButtonClicked = React.useCallback(() => {
        if (enableAnimation.current) {
            toggleGroupWithAnimation();
        } else {
            toggleGroupWithoutAnimation();
        }

        // isCollapsed reflects the state *before* the toggle.
        // If this section has focus and it is being collapsed, we move focus to the toggle button in the header
        if (!isCollapsed && hasFocus(ref)) {
            listListener.onCollapsed();
        }
    }, [isCollapsed, toggleGroupWithAnimation, toggleGroupWithoutAnimation, listListener]);

    React.useImperativeHandle(
        componentRef,
        () => ({
            scrollToElement: (position: SectionScrollPosition): void => {
                if (isCollapsed) {
                    // expand the group first (without animation)
                    toggleGroupWithoutAnimation();

                    // delay 0ms to wait for the group to expand before scrolling to the index
                    setTimeout(() => listListener.scrollToAndFocus(position), 0);
                } else {
                    listListener.scrollToAndFocus(position);
                }
            },
            get hasFocus(): boolean {
                return hasFocus(ref);
            },
        }),
        [toggleGroupWithoutAnimation, listListener, isCollapsed]
    );

    const [itemsToRender, shouldAnimateItemEntrance, renderIfEmptyItem] =
        useAnimateListModifications(
            enableAnimation.current,
            items,
            getGroupedComboBoxItemKey,
            createEmptyGroupedComboBoxItem,
            itemHeight
        );

    const hasIcon = items.some((item) => item.icon);
    const onRenderCell = React.useCallback(
        (item?: IGroupedComboBoxItem, index?: number): React.ReactNode => {
            if (!item) {
                return null;
            }

            const emptyItem = renderIfEmptyItem(index);
            if (emptyItem !== undefined) {
                return emptyItem;
            }

            // Optimization: We only set the 'selectedKey' if this item (or one of the sub items) is selected. Otherwise, set undefined.
            // By doing this, if 'selectedKey' is changed on the GroupedComboBox, we only need to re-render two items, the old selected item and the new one.
            const selectedKeyToUse = shouldHighlightItem(item, selectedKey)
                ? selectedKey
                : undefined;

            // If this index was newly added to the list, we should enable its entrance animation
            const animateEntrance = shouldAnimateItemEntrance(index);

            return (
                <GroupedComboBoxButton
                    item={item}
                    styles={itemStyles}
                    reserveSpaceForIcon={hasIcon}
                    key={item.itemKey}
                    parentGroupKey={groupKey}
                    selectedKey={selectedKeyToUse}
                    animateEntrance={animateEntrance}
                    itemHeight={itemHeight}
                />
            );
        },
        [
            renderIfEmptyItem,
            selectedKey,
            shouldAnimateItemEntrance,
            itemStyles,
            hasIcon,
            groupKey,
            itemHeight,
        ]
    );

    const rootId = useId('GroupedComboBoxSection');

    // Because selectedKey is not set to any prop of <List>, when selectedKey changes, <List> doesn't re-render.
    React.useEffect(() => listRef.current?.forceUpdate(), [selectedKey]);

    const onPagesUpdated = React.useCallback(
        (pages: IPage[]) => listListener.onPageUpdates(pages),
        [listListener]
    );

    return (
        <div id={rootId} ref={ref}>
            <GroupHeader
                group={props}
                isCollapsed={isCollapsed}
                styles={headerStyles}
                onToggleCollapse={onToggleButtonClicked}
            />

            <div
                ref={listWrapperRef}
                style={getStyles(isCollapsed ?? false, maxHeight)}
                onTransitionEnd={onListTransitionEnd}
            >
                {(maxHeight !== undefined || !isCollapsed) && (
                    <List
                        role="group"
                        aria-label={text}
                        componentRef={listRef}
                        onPagesUpdated={onPagesUpdated}
                        items={itemsToRender}
                        onRenderCell={onRenderCell}
                        getKey={getGroupedComboBoxItemKey}
                        ignoreScrollingState={true}
                        renderedWindowsAhead={1}
                        renderedWindowsBehind={1}
                    />
                )}
            </div>
        </div>
    );
};

Section.displayName = 'Section';
